import { REPORTS_MAX_RANGE_DAYS } from "./utils"
import { getUtcEndOfDay, getUtcStartOfDay } from "../../../utils/dateFunctions"
import { IsoDateTime } from "@figurepos/fgr-lib-business-logic/lib/common/dateTime/types"
import { differenceInCalendarDays, subDays } from "date-fns"

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+\-]\d{2}:\d{2})?)?$/

export const formatLocalYmd = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${date.getFullYear()}-${month}-${day}`
}

export const parseDateParam = (raw: unknown): string | null => {
    if (typeof raw !== "string" || raw.length > 40) {
        return null
    }
    if (!ISO_DATE_RE.test(raw)) {
        return null
    }
    // Parse as date and check validity strictly
    const d = new Date(raw)
    if (isNaN(d.getTime())) {
        return null
    }
    // Extract what the date would be after parsing; YY-MM-DD portion
    const parts = /^\d{4}-\d{2}-\d{2}/.exec(raw)
    if (!parts) {
        return null
    }
    const [y, m, day] = parts[0].split("-")
    if (d.getUTCFullYear() !== Number(y) || d.getUTCMonth() + 1 !== Number(m) || d.getUTCDate() !== Number(day)) {
        return null
    }
    return raw
}

type UrlDateRange = { from?: unknown; to?: unknown }

export const sanitizeUrlDateRange = (
    { from, to }: UrlDateRange,
    options: { midnightOffsetMinutes: number; maxDays?: number }
): {
    timestampFrom: IsoDateTime
    timestampTo: IsoDateTime
    dateFrom: string
    dateTo: string
} | null => {
    const maxDays = options.maxDays ?? REPORTS_MAX_RANGE_DAYS
    const fromParsed = parseDateParam(from)
    const toParsed = parseDateParam(to)
    if (!fromParsed || !toParsed) {
        return null
    }

    let fromDate = fromParsed
    let toDate = toParsed

    // Ensure fromDate is NOT after toDate
    const fromDateObj = new Date(fromDate)
    const toDateObj = new Date(toDate)
    if (fromDateObj > toDateObj) {
        return null
    }

    // Clamp to today (no future dates)
    const today = new Date()
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    if (new Date(toDate).getTime() > todayDate.getTime()) {
        toDate = formatLocalYmd(todayDate)
    }
    if (new Date(fromDate).getTime() > todayDate.getTime()) {
        fromDate = formatLocalYmd(todayDate)
    }

    // Clamp span to maxDays (inclusive) using calendar days
    const daysDiff = Math.abs(differenceInCalendarDays(toDateObj, fromDateObj)) + 1
    if (daysDiff > maxDays) {
        fromDate = formatLocalYmd(subDays(toDateObj, maxDays - 1))
    }

    return {
        timestampFrom: getUtcStartOfDay(fromDate, null, options.midnightOffsetMinutes),
        timestampTo: getUtcEndOfDay(toDate, null, options.midnightOffsetMinutes),
        dateFrom: fromDate,
        dateTo: toDate,
    }
}

export const sanitizeSearchText = (input: unknown, options?: { maxLength?: number }): string | undefined => {
    const maxLength = options?.maxLength ?? 200
    if (typeof input !== "string") {
        return undefined
    }
    // Normalize to canonical form
    const normalized = input.normalize("NFC")
    // Remove control chars (ASCII C0 + DEL), zero-widths, and collapse whitespace
    const withoutControls = normalized
        .replace(/[\u0000-\u001F\u007F]/g, "")
        .replace(/[\u200B-\u200F\u202A-\u202E\u2060-\u206F\uFEFF]/g, "")
        .replace(/\s+/g, " ")
    const trimmed = withoutControls.trim()
    // Block common XSS vectors early
    if (/[<>`]/.test(trimmed)) {
        return undefined
    }
    if (trimmed.length === 0 || trimmed.length > maxLength) {
        return undefined
    }
    return trimmed
}
